import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { ThemeService } from '../../services/theme.service';
import { LayoutService } from '../../services/layout.service';
import { JwtAuthService } from '../../services/auth/jwt-auth.service';
import { LanguageService, Language } from '../../services/language.service';
import { Utils } from '../../../utils/utils';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AppInfoService } from '../../services/app-info.service';
import { AboutDialogComponent } from '../about-dialog/about-dialog.component';

@Component({
  selector: 'app-header-side',
  templateUrl: './header-side.template.html',
  styleUrls: ['./header-side.component.scss']
})
export class HeaderSideComponent implements OnInit, OnDestroy {
  @Input() notificPanel;
  public availableLangs: Language[];
  public currentLang: Language;
  utils = Utils;
  public egretThemes;
  public layoutConf: any;
  private langSub: Subscription;

  constructor(
    private themeService: ThemeService,
    private layout: LayoutService,
    public jwtAuth: JwtAuthService,
    private languageService: LanguageService,
    public appInfo: AppInfoService,
    private dialog: MatDialog
  ) {}

  openAbout() {
    this.dialog.open(AboutDialogComponent, { autoFocus: false });
  }

  ngOnInit() {
    this.egretThemes = this.themeService.egretThemes;
    this.layoutConf = this.layout.layoutConf;
    this.availableLangs = this.languageService.getAvailableLangs();

    this.langSub = this.languageService.currentLang$.subscribe((lang: Language) => {
      this.currentLang = lang;
    });
  }

  ngOnDestroy() {
    if (this.langSub) {
      this.langSub.unsubscribe();
    }
  }

  setLang(lng: Language) {
    this.languageService.setLang(lng);
  }

  toggleNotificationPanel() {
    if (this.notificPanel) {
      this.notificPanel.toggle();
    }
  }

  toggleSidenav() {
    this.layout.publishLayoutChange({
      sidebarStyle: this.layoutConf.sidebarStyle === 'closed' ? 'full' : 'closed'
    });
  }

  openExternalApp(url: string) {
    window.open(url, '_blank');
  }

  /**
   * Empresa da sessão.
   *
   * Em acesso remoto (intranet), `filial$` vem de `/auth/me/filial` na API CENTRAL — ou seja,
   * traz a filial da Infokaw, não a empresa em que o usuário está trabalhando. Quem descreve a
   * empresa remota é `item.empresaRemota`. Por isso a fonte é escolhida uma vez aqui e todos os
   * campos saem dela: misturar as duas era o que mostrava o CNPJ/cidade da central no cabeçalho.
   */
  private get empresaSessao(): any {
    const item = this.jwtAuth.item$?.value;
    if (item?.remoto && item?.empresaRemota) {
      return item.empresaRemota;
    }
    return this.jwtAuth.filial$?.value ?? item?.empresaRemota ?? null;
  }

  get empresaNomeSessao(): string {
    const empresa = this.empresaSessao;
    return empresa?.nomeFantasia || empresa?.razaoSocial || '';
  }

  /** Código da empresa/filial em sessão — é por ele que o usuário a identifica nos lançamentos. */
  get empresaCodigoSessao(): string {
    const item = this.jwtAuth.item$?.value;
    const empresa = this.empresaSessao;
    const codigo = (item?.remoto && item?.empresaRemota)
      ? empresa?.codigo
      : (empresa?.id ?? empresa?.codigo ?? item?.filialCodigo ?? item?.filialId);
    return codigo !== null && codigo !== undefined && `${codigo}`.trim() !== '' ? `${codigo}` : '';
  }

  get empresaRazaoSocialSessao(): string {
    return this.empresaSessao?.razaoSocial || '';
  }

  /** CNPJ/CPF já formatado (o helper aceita CNPJ alfanumérico). */
  get empresaDocumentoSessao(): string {
    const empresa = this.empresaSessao;
    const doc = empresa?.inscricaoFederal || empresa?.cpf_cnpj || empresa?.cnpj;
    return doc ? `${Utils.convertToCpfCnpj(doc)}`.trim() : '';
  }

  get empresaCidadeUfSessao(): string {
    const municipio = this.empresaSessao?.cadMun;
    if (!municipio?.municipio) {
      return '';
    }
    return municipio.uf ? `${municipio.municipio}/${municipio.uf}` : `${municipio.municipio}`;
  }

  /**
   * Linha de identificação da empresa na barra: código e nome. É o que responde
   * "em qual empresa eu estou?" sem abrir o cadastro — pergunta frequente em base multi-filial.
   */
  get empresaResumoSessao(): string {
    const codigo = this.empresaCodigoSessao;
    const nome = this.empresaNomeSessao;
    return [codigo ? `[${codigo}]` : '', nome].filter(Boolean).join(' ');
  }

  /** Segunda linha (só no desktop): documento e praça da empresa. */
  get empresaSubtituloSessao(): string {
    return [this.empresaDocumentoSessao, this.empresaCidadeUfSessao].filter(Boolean).join(' · ');
  }

  /** Detalhamento completo no tooltip — o que não cabe na barra. */
  get empresaDetalheSessao(): string {
    const empresa = this.empresaSessao;
    const item = this.jwtAuth.item$?.value;
    const linhas = [
      this.empresaRazaoSocialSessao,
      this.empresaCodigoSessao ? `Empresa/Filial: ${this.empresaCodigoSessao}` : '',
      this.empresaDocumentoSessao ? `CNPJ/CPF: ${this.empresaDocumentoSessao}` : '',
      empresa?.inscricaoEstadual ? `IE: ${empresa.inscricaoEstadual}` : '',
      empresa?.inscricaoMunicipal ? `IM: ${empresa.inscricaoMunicipal}` : '',
      empresa?.regimeTributacao ? `Regime: ${empresa.regimeTributacao}` : '',
      this.empresaCidadeUfSessao ? `Município: ${this.empresaCidadeUfSessao}` : '',
      empresa?.paramDfAmbiente ? `Ambiente DF-e: ${empresa.paramDfAmbiente}` : '',
      empresa?.filialAccess ? `Filiais liberadas: ${empresa.filialAccess}` : '',
      item?.remoto ? 'Acesso remoto (intranet)' : '',
      item?.userName ? `Usuário: ${item.userName}` : ''
    ];
    return linhas.filter(Boolean).join('\n');
  }
}
